import { mapEadinStatus } from './receivedUtils';
import { derivePoStatus, syncLineItemsReceivedFromBoxes } from './receivingStatus';

export const RECEIVING_WAREHOUSE = 'Las Vegas, NV';

function poNumeric(poNumber) {
  const digits = String(poNumber || '').replace(/\D/g, '');
  return parseInt(digits, 10) || 0;
}

function deriveBoxState(expected, scanned, handCount) {
  if (handCount != null && handCount !== '') return 'override_received';
  if (scanned == null || scanned <= 0) return 'pending';
  if (scanned === expected) return 'complete';
  if (scanned < expected) return 'short';
  return 'over';
}

function buildBoxesFromProducts(products) {
  const boxes = [];
  let boxNumber = 1;
  for (const product of products || []) {
    for (const box of product.boxes || []) {
      const expected = Number(box.quantity) || Number(product.quantity) || 0;
      const received = Number(box.receivedQuantity) || 0;
      if (expected <= 0) continue;
      boxes.push({
        id: String(box.id || `${product.id}-B${boxNumber}`),
        inboundType: 'box',
        productId: product.id,
        boxNumber,
        sku: product.sku,
        shipment: box.shipment || null,
        expectedUnits: expected,
        receivedUnits: received,
        scannedUnits: null,
        handCountUnits: null,
        state: deriveBoxState(expected, null, null),
      });
      boxNumber += 1;
    }
  }
  return boxes;
}

/** Container POs (e.g. PO 424) — each shipment_container_item is one receivable unit. */
function buildBoxesFromContainers(containers, products = []) {
  const skuToProductId = new Map((products || []).map((p) => [p.sku, p.id]));
  const boxes = [];
  let boxNumber = 1;
  for (const container of containers || []) {
    for (const item of container.items || []) {
      const expected = Number(item.quantity) || 0;
      if (expected <= 0) continue;
      const received = Number(item.receivedQuantity) || 0;
      boxes.push({
        id: String(item.id),
        inboundType: 'container',
        containerItemId: item.id,
        containerId: container.id,
        containerName: container.name || '',
        productId: skuToProductId.get(item.sku) || item.productId || null,
        boxNumber,
        sku: item.sku,
        shipment: item.shipment || container.shipment || null,
        expectedUnits: expected,
        receivedUnits: received,
        scannedUnits: null,
        handCountUnits: null,
        state: deriveBoxState(expected, null, null),
      });
      boxNumber += 1;
    }
  }
  return boxes;
}

function buildBoxesFromShipment(shipment) {
  const products = shipment?.products || [];
  const fromContainers = buildBoxesFromContainers(shipment?.containers, products);
  if (fromContainers.length > 0) return fromContainers;
  return buildBoxesFromProducts(products);
}

function buildLineItemsFromShipment(products) {
  return (products || []).map((p) => ({
    sku: p.sku,
    productName: p.name || p.sku,
    variant: '',
    expectedUnits: Number(p.quantity) || 0,
    receivedUnits: Number(p.quantityReceived) || 0,
    cartonQty: Number(p.carton_qty) || Number(p.quantity) || 1,
    boxIds: (p.boxes || []).map((b) => String(b.id)),
  }));
}

function matchBoxIdForLog(items, boxes = []) {
  if (!boxes.length) return null;
  const scannedSkus = (items || [])
    .filter((i) => (i.scannedUnits || i.scanned_quantity || 0) > 0)
    .map((i) => i.sku || i.product?.sku)
    .filter(Boolean);
  for (const sku of scannedSkus) {
    const box = boxes.find((b) => b.sku === sku);
    if (box) return box.id;
  }
  return boxes[0]?.id || null;
}

/** Sum scanned_quantity from PO received logs, keyed by SKU. */
function scannedBySkuFromLogs(logs = []) {
  const bySku = new Map();
  for (const log of logs || []) {
    for (const item of log.items || []) {
      const sku = item.product?.sku || item.sku;
      if (!sku) continue;
      const qty = Number(item.scanned_quantity ?? item.scannedUnits) || 0;
      if (qty <= 0) continue;
      bySku.set(sku, (bySku.get(sku) || 0) + qty);
    }
  }
  return bySku;
}

/**
 * Apply historical PO-level scan totals onto boxes (old scans lived only in po_received_logs).
 * Prefer existing box.scannedUnits when already set (e.g. from BoxReceivedLog / live capture).
 */
function applyScannedUnitsToBoxes(boxes, scannedBySku) {
  if (!boxes?.length || !scannedBySku?.size) return boxes || [];

  const explicitBySku = new Map();
  for (const box of boxes) {
    if (box.scannedUnits == null || box.scannedUnits <= 0) continue;
    explicitBySku.set(box.sku, (explicitBySku.get(box.sku) || 0) + Number(box.scannedUnits));
  }

  const remainderBySku = new Map();
  for (const [sku, total] of scannedBySku) {
    remainderBySku.set(
      sku,
      Math.max(0, Number(total) - (explicitBySku.get(sku) || 0)),
    );
  }

  const next = boxes.map((box) => ({
    ...box,
    state: deriveBoxState(box.expectedUnits, box.scannedUnits, box.handCountUnits),
  }));

  for (const [sku, totalRemainder] of remainderBySku) {
    if (totalRemainder <= 0) continue;
    const candidates = next.filter(
      (box) => box.sku === sku && (box.scannedUnits == null || box.scannedUnits <= 0),
    );
    let remaining = totalRemainder;
    candidates.forEach((box, index) => {
      if (remaining <= 0) return;
      const expected = Number(box.expectedUnits) || 0;
      const isLast = index === candidates.length - 1;
      const assigned = isLast ? remaining : Math.min(remaining, expected);
      if (assigned <= 0) return;
      box.scannedUnits = assigned;
      box.state = deriveBoxState(box.expectedUnits, assigned, box.handCountUnits);
      remaining -= assigned;
    });
  }

  return next;
}

export function mapLogToSession(log, poDisplayId, boxes = []) {
  const items = (log.items || []).map((item) => ({
    sku: item.product?.sku || '',
    productName: item.product?.name || '',
    expectedUnits: item.expected_quantity || 0,
    scannedUnits: item.scanned_quantity || 0,
  }));
  const matched = log.total_matched || 0;
  const totalExpected = items.reduce((s, i) => s + i.expectedUnits, 0);
  let result = 'complete';
  if (matched < totalExpected) result = 'short';
  else if (matched > totalExpected) result = 'over';

  const boxId = matchBoxIdForLog(items, boxes);

  return {
    id: `log-${log.id}`,
    logId: log.id,
    poId: poDisplayId,
    boxId,
    timestamp: log.date_received,
    scannedBy: log.packer?.full_name || 'Unknown',
    rawReads: log.total_scanned || 0,
    // uniqueEpcs is optional in legacy schema; until stored, show matched as best available
    uniqueEpcs: log.total_unique ?? matched,
    matched,
    result,
    items,
    notes: log.notes || undefined,
  };
}

export function buildPoFromShipment(shipment, logs = [], existing) {
  const displayId = shipment.poNumber || shipment.po_number;
  const lineItems = buildLineItemsFromShipment(shipment.products);
  let boxes = buildBoxesFromShipment(shipment);
  if (!boxes.length && lineItems.length) {
    boxes = lineItems.map((li, i) => ({
      id: `${displayId}-line-${i}`,
      boxNumber: i + 1,
      sku: li.sku,
      expectedUnits: li.expectedUnits,
      receivedUnits: li.receivedUnits || 0,
      scannedUnits: null,
      handCountUnits: null,
      state: deriveBoxState(li.expectedUnits, null, null),
    }));
  }
  if (existing?.boxes?.length) {
    const byId = new Map(existing.boxes.map((b) => [b.id, b]));
    boxes = boxes.map((b) => {
      const existingBox = byId.get(b.id);
      return existingBox
        ? { ...b, ...existingBox, shipment: b.shipment || existingBox.shipment || null }
        : b;
    });
  }
  const poLogs = logs.filter(
    (l) => l.po_id === shipment.id || l.purchaseOrder?.id === shipment.id,
  );
  const scanSessions = poLogs
    .map((l) => mapLogToSession(l, displayId, boxes))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  const cumulativeBySku = new Map();
  for (const product of shipment.products || []) {
    if (product.cumulativeScanned != null && product.sku) {
      cumulativeBySku.set(product.sku, Number(product.cumulativeScanned) || 0);
    }
  }
  boxes = applyScannedUnitsToBoxes(
    boxes,
    cumulativeBySku.size > 0 ? cumulativeBySku : scannedBySkuFromLogs(poLogs),
  );

  const totalExpected = lineItems.reduce((s, li) => s + li.expectedUnits, 0);
  const totalReceived = lineItems.reduce((s, li) => s + li.receivedUnits, 0);

  const po = {
    id: displayId,
    poDbId: shipment.id,
    poNumber: poNumeric(displayId),
    supplier: shipment.vendor || shipment.vendor_name || 'Unknown',
    asnRef: shipment.containers?.[0]?.name || shipment.shipmentDate || '',
    warehouse: RECEIVING_WAREHOUSE,
    eadinStatus: mapEadinStatus(shipment),
    receiptClosedAt: shipment.receipt_closed_at || null,
    status: 'expected',
    expectedDate: dateOnlyFromShipment(shipment),
    receivedDate: totalReceived >= totalExpected && totalExpected > 0 ? todayIso() : null,
    assignee: existing?.assignee ?? null,
    nextAction: existing?.nextAction || (totalReceived > 0 ? 'Continue receiving' : 'Scan boxes'),
    docsComplete: existing?.docsComplete ?? true,
    lineItems,
    boxes,
    exceptions: existing?.exceptions || [],
    scanSessions,
    attachments: existing?.attachments || [],
    notes: existing?.notes || [],
    activity: existing?.activity || [],
    createdAt: shipment.dateCreated || shipment.po_date || todayIso(),
  };
  const synced = syncLineItemsReceivedFromBoxes(po);
  synced.status = derivePoStatus(synced);
  return synced;
}

function dateOnlyFromShipment(shipment) {
  const raw =
    shipment.estimatedDeliveryDate ||
    shipment.estimatedDelivery ||
    shipment.dateCreated ||
    shipment.po_date;
  if (!raw) return todayIso().slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString();
}

/** Map UI status filter → API statuses param for GET /api/v1/shipments */
export function buildQueueStatusParams(statusFilter, showClosed) {
  const byFilter = {
    open: ['OPEN'],
    closed: ['CLOSED'],
    canceled: ['CANCELLED'],
    draft: ['DRAFT'],
  };
  if (statusFilter && statusFilter !== 'all') {
    return byFilter[statusFilter] || [];
  }
  // All statuses: exclude CLOSED unless the operator opted in
  if (showClosed) return [];
  return ['OPEN', 'CANCELLED'];
}

/**
 * Queue-specific hydration: inbound shipments POs (eden_status ≠ DELETED via API),
 * with server-side search + PO status filter (OPEN / CLOSED / CANCELLED / …).
 * Callers must fetch shipments + logs via boxReceivedSlice thunks.
 */
export function buildReceivingQueueFromApi({
  shipmentsData,
  logs = [],
  existingPos = [],
  page = 1,
} = {}) {
  const shipments = shipmentsData?.items || [];
  const logsByPo = new Map();
  for (const log of logs || []) {
    const poId = log.po_id || log.purchaseOrder?.id;
    if (!logsByPo.has(poId)) logsByPo.set(poId, []);
    logsByPo.get(poId).push(log);
  }
  const existingById = new Map((existingPos || []).map((p) => [p.id, p]));

  const pos = shipments.map((shipment) =>
    buildPoFromShipment(
      shipment,
      logsByPo.get(shipment.id) || [],
      existingById.get(shipment.poNumber),
    ),
  );

  pos.sort((a, b) => b.poNumber - a.poNumber);

  return {
    pos,
    pagination: {
      page: shipmentsData?.page ?? page,
      totalPages: shipmentsData?.totalPages ?? 1,
      rangeRows: shipmentsData?.rangeRows ?? '',
      isTheLastPage: shipmentsData?.isTheLastPage ?? true,
    },
  };
}

/** @deprecated Prefer buildReceivingQueueFromApi after slice fetches */
export async function fetchReceivingQueue({
  search = '',
  statusFilter = 'all',
  showClosed = false,
  page = 1,
  itemsPerPage = 300,
  existingPos = [],
  fetchShipments,
  fetchLogs,
} = {}) {
  const statuses = buildQueueStatusParams(statusFilter, showClosed);
  const params = { page, itemsPerPage };
  if (search.trim()) params.search = search.trim();
  if (statuses.length > 0) params.statuses = statuses;

  if (!fetchShipments || !fetchLogs) {
    throw new Error('fetchReceivingQueue requires fetchShipments and fetchLogs from boxReceivedSlice');
  }

  const [shipmentsData, logs] = await Promise.all([
    fetchShipments(params),
    fetchLogs(),
  ]);

  return buildReceivingQueueFromApi({
    shipmentsData,
    logs,
    existingPos,
    page,
  });
}

/** @deprecated Use fetchReceivingQueue — kept for scan-log refresh paths */
export async function hydrateReceivingPos(existingPos = [], fetchers) {
  return fetchReceivingQueue({
    statusFilter: 'all',
    showClosed: true,
    existingPos,
    ...fetchers,
  });
}

function boxesFromLineItems(poId, lineItems, existingBoxes = []) {
  const existing = existingBoxes || [];
  // Keep real shipment boxes; append a synthetic card for any line SKU not covered
  if (existing.length > 0) {
    const covered = new Set(existing.map((b) => b.sku));
    const extras = [];
    let nextNum = existing.reduce((m, b) => Math.max(m, b.boxNumber || 0), 0) + 1;
    for (const li of lineItems) {
      if (covered.has(li.sku)) continue;
      extras.push({
        id: `${poId}-line-${li.sku}`,
        boxNumber: nextNum++,
        sku: li.sku,
        expectedUnits: li.expectedUnits,
        receivedUnits: li.receivedUnits || 0,
        scannedUnits: li.scannedUnits ?? null,
        handCountUnits: null,
        state: deriveBoxState(li.expectedUnits, li.scannedUnits ?? null, null),
      });
      covered.add(li.sku);
    }
    return [
      ...existing.map((b) => {
        const li = lineItems.find((x) => x.sku === b.sku);
        if (!li) return b;
        const scanned =
          b.scannedUnits != null && b.scannedUnits > 0
            ? b.scannedUnits
            : null;
        return {
          ...b,
          expectedUnits: b.expectedUnits || li.expectedUnits,
          receivedUnits: b.receivedUnits != null ? b.receivedUnits : (li.receivedUnits || 0),
          scannedUnits: scanned,
          state: deriveBoxState(
            b.expectedUnits || li.expectedUnits,
            scanned,
            b.handCountUnits,
          ),
        };
      }),
      ...extras,
    ];
  }
  return lineItems.map((li, i) => ({
    id: `${poId}-line-${i}`,
    boxNumber: i + 1,
    sku: li.sku,
    expectedUnits: li.expectedUnits,
    receivedUnits: li.receivedUnits || 0,
    scannedUnits: li.scannedUnits ?? null,
    handCountUnits: null,
    state: deriveBoxState(li.expectedUnits, li.scannedUnits ?? null, null),
  }));
}

export function applySearchPoToReceivingPo(po, data, logs) {
  const lineItems = (data.line_items || []).map((li) => ({
    sku: li.sku,
    productName: li.name || li.sku,
    variant: '',
    expectedUnits: li.expected_quantity || 0,
    // Inbound received stays on shipment boxes; search-po cumulative is scan history
    receivedUnits: Number(li.quantity_received) || 0,
    scannedUnits: Number(li.cumulative_scanned) || 0,
    cartonQty: li.expected_quantity || 1,
    boxIds: [],
  }));
  let boxes = boxesFromLineItems(po.id, lineItems, po.boxes);

  // Apply cumulative_scanned from search-po (covers old scans even if log items aren't loaded)
  const fromSearch = new Map();
  for (const li of data.line_items || []) {
    const qty = Number(li.cumulative_scanned) || 0;
    if (li.sku && qty > 0) fromSearch.set(li.sku, qty);
  }
  const poLogs = (logs || []).filter((l) => l.po_id === data.id);
  const fromLogs = scannedBySkuFromLogs(poLogs);
  // Prefer higher of the two sources per SKU
  const merged = new Map(fromSearch);
  for (const [sku, qty] of fromLogs) {
    merged.set(sku, Math.max(merged.get(sku) || 0, qty));
  }
  boxes = applyScannedUnitsToBoxes(boxes, merged);

  // Preserve inbound received from shipment boxes when search-po has no quantity_received
  boxes = boxes.map((b) => {
    const existing = (po.boxes || []).find((x) => x.id === b.id);
    if (existing?.receivedUnits != null && (b.receivedUnits == null || b.receivedUnits === 0)) {
      return { ...b, receivedUnits: existing.receivedUnits };
    }
    return b;
  });

  const updated = {
    ...po,
    poDbId: data.id,
    eadinStatus: mapEadinStatus(data.status),
    supplier: po.supplier || data.vendor_name || 'Unknown',
    lineItems,
    boxes,
    scanSessions: poLogs
      .map((l) => mapLogToSession(l, po.id, boxes))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
  };
  const synced = syncLineItemsReceivedFromBoxes(updated);
  synced.status = derivePoStatus(synced);
  return synced;
}

/** @deprecated Prefer searchPoReceivedLogRequest + applySearchPoToReceivingPo */
export async function refreshPoFromApi(po, logs, searchPo) {
  if (!searchPo) {
    throw new Error('refreshPoFromApi requires searchPo from boxReceivedSlice');
  }
  const data = await searchPo(po.id);
  return applySearchPoToReceivingPo(po, data, logs);
}

/** Load a PO that may not be on the current queue page (scan-log drawer). */
export function buildStubPoForInspector(poNumber) {
  return {
    id: poNumber,
    poDbId: null,
    poNumber: poNumeric(poNumber),
    supplier: 'Unknown',
    asnRef: '',
    warehouse: RECEIVING_WAREHOUSE,
    eadinStatus: 'open',
    status: 'expected',
    expectedDate: todayIso().slice(0, 10),
    receivedDate: null,
    assignee: null,
    nextAction: 'Scan boxes',
    docsComplete: true,
    lineItems: [],
    boxes: [],
    exceptions: [],
    scanSessions: [],
    attachments: [],
    notes: [],
    activity: [],
    createdAt: todayIso(),
  };
}

/** @deprecated Prefer buildStubPoForInspector + applySearchPoToReceivingPo */
export async function loadPoForInspector(poNumber, logs, searchPo) {
  const stub = buildStubPoForInspector(poNumber);
  return refreshPoFromApi(stub, logs || [], searchPo);
}
